import * as React from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    CircularProgress,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { getSearchLeagues } from '../Api';
import { PaginatedLeagueInfoResponseDto } from '../models/dtos/LeagueInfoDto';

type LeagueOption = {
    id: number;
    name: string;
    sportName?: string;
};

type QuizLeaguePickerProps = {
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    sportId?: number | null;
};

export default function QuizLeaguePicker({ selectedIds, onChange, sportId }: QuizLeaguePickerProps) {
    const [searchTerm, setSearchTerm] = React.useState('');
    const [options, setOptions] = React.useState<LeagueOption[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [knownLeagues, setKnownLeagues] = React.useState<Record<number, LeagueOption>>({});

    React.useEffect(() => {
        if (searchTerm.trim().length < 2) {
            setOptions([]);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getSearchLeagues(searchTerm.trim()).then((result: PaginatedLeagueInfoResponseDto) => {
            if (cancelled) return;
            const mapped = (result.items ?? [])
                .filter(item => sportId == null || item.sport?.id === sportId)
                .map(item => ({
                    id: item.id,
                    name: item.name ?? `League ${item.id}`,
                    sportName: item.sport?.name ?? undefined,
                }));
            setOptions(mapped);
            setKnownLeagues(prev => {
                const next = { ...prev };
                mapped.forEach(l => { next[l.id] = l; });
                return next;
            });
            setLoading(false);
        }).catch(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [searchTerm, sportId]);

    const selectedLeagues = selectedIds.map(id => knownLeagues[id] ?? { id, name: `League #${id}` });

    const addLeague = (league: LeagueOption | null) => {
        if (league == null || selectedIds.includes(league.id)) return;
        setKnownLeagues(prev => ({ ...prev, [league.id]: league }));
        onChange([...selectedIds, league.id]);
        setSearchTerm('');
    };

    const removeLeague = (id: number) => {
        onChange(selectedIds.filter(x => x !== id));
    };

    return (
        <Stack spacing={1}>
            <Autocomplete
                options={options}
                loading={loading}
                inputValue={searchTerm}
                onInputChange={(_, value) => setSearchTerm(value)}
                onChange={(_, value) => addLeague(value)}
                getOptionLabel={(option) => `${option.name} (#${option.id})`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search leagues"
                        placeholder="Type at least 2 characters"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedLeagues.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No leagues selected</Typography>
                ) : (
                    selectedLeagues.map(league => (
                        <Chip
                            key={league.id}
                            label={`${league.name} (#${league.id})`}
                            onDelete={() => removeLeague(league.id)}
                        />
                    ))
                )}
            </Box>
        </Stack>
    );
}
